import { IconLayer } from '@deck.gl/layers';
import type { LayerState, LayerFeature, PowerOutageProps } from '@vitals/shared';
import type { RendererDef } from './registry.js';
import { outageGlyph, type IconDef } from './markerGlyphs.js';

const outageProps = (f: LayerFeature): PowerOutageProps =>
  f.props as unknown as PowerOutageProps;

interface OutageTier {
  /** Stable cache key for the baked glyph. */
  key: string;
  /** Badge size in pixels — bigger outage, bigger marker. */
  size: number;
  /** Badge color: amber (informational) → orange → red (severe). */
  color: [number, number, number];
}

/** Map customers-out to a badge tier (size + color). */
function outageTier(customersOut: number): OutageTier {
  if (customersOut <= 0) return { key: '0', size: 20, color: [245, 190, 48] };
  if (customersOut < 50) return { key: '1', size: 24, color: [245, 140, 30] };
  if (customersOut < 200) return { key: '2', size: 28, color: [245, 140, 30] };
  if (customersOut < 1000) return { key: '3', size: 32, color: [240, 100, 40] };
  return { key: '4', size: 38, color: [220, 50, 50] };
}

interface OutageDatum {
  position: [number, number];
  icon: IconDef;
  size: number;
  props: PowerOutageProps;
}

/**
 * Build an IconLayer of ConEd outage clusters, each a lightning-bolt badge
 * sized and colored by customers-out. Returns null when feed is down or empty.
 */
export function buildPowerOutagesLayer(
  state: LayerState,
  _nowSec: number,
): IconLayer<OutageDatum> | null {
  if (!state || state.status === 'down' || state.features.length === 0) return null;

  const data: OutageDatum[] = state.features.map((f) => {
    const p = outageProps(f);
    const tier = outageTier(p.customersOut);
    return {
      position: (f.geometry as { coordinates: [number, number] }).coordinates,
      icon: outageGlyph(tier.key, tier.color, tier.size),
      size: tier.size,
      props: p,
    };
  });

  return new IconLayer<OutageDatum>({
    id: 'power_outages',
    data,
    getIcon: (d) => d.icon,
    getPosition: (d) => d.position,
    getSize: (d) => d.size,
    sizeUnits: 'pixels',
    billboard: true,
    pickable: true,
  });
}

export const powerOutagesRenderer: RendererDef = {
  layerId: 'power_outages',
  build: (state, nowSec) => buildPowerOutagesLayer(state, nowSec),
};
